import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PdfExportButton } from '@/components/shared/PdfExportButton';
import { Button } from '@/components/ui/button';
import { getMethodView } from '@/components/methodologies/views';
import type { CatalogMethod, MethodologyArtifact } from '@/types/methodologies';

interface MethodologyResultPanelProps {
  methodId: string;
  catalogMethod: CatalogMethod | undefined;
  artifact: MethodologyArtifact;
  projectName: string;
  canDelete: boolean;
  onDelete: () => void;
  onReview?: () => void;
}

/**
 * Detail view for one artifact. Mirrors `AiDetailPanel`'s chrome (title,
 * delete, PDF export) but renders the structured `content` through the
 * layout registry instead of markdown — the layouts are what make Lean
 * Canvas/SWOT/CJM readable, `generated_text` exists only for PDF/markdown.
 */
export function MethodologyResultPanel({
  methodId,
  catalogMethod,
  artifact,
  projectName,
  canDelete,
  onDelete,
  onReview,
}: MethodologyResultPanelProps) {
  const { t } = useTranslation('methodologies');
  const [tooltipVisible, setTooltipVisible] = useState(false);

  const title = artifact.title || t(`methods.${methodId}.title`);
  const ViewComponent = catalogMethod ? getMethodView(catalogMethod.layout) : null;

  return (
    <div className="flex-1 min-w-0 [overflow-wrap:anywhere]">
      <div className="bg-[#f4f4f3] rounded-[16px] p-[20px] md:p-[40px] flex flex-col gap-[24px]">
        <div className="flex items-start justify-between gap-[16px]">
          <h2 className="font-manrope font-bold text-[24px] md:text-[32px] leading-[32px] md:leading-[40px] text-[#0d0d0d] flex-1 break-words">
            {title}
          </h2>
          <button
            type="button"
            onClick={() => canDelete && onDelete()}
            disabled={!canDelete}
            onMouseEnter={() => !canDelete && setTooltipVisible(true)}
            onMouseLeave={() => setTooltipVisible(false)}
            aria-label={t('detail.deleteTooltip')}
            className={`relative w-[24px] h-[24px] flex-shrink-0 ${!canDelete ? 'cursor-not-allowed' : 'cursor-pointer hover:opacity-70'} transition-opacity`}
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M21 5.98047C17.67 5.65047 14.32 5.48047 10.98 5.48047C9 5.48047 7.02 5.58047 5.04 5.78047L3 5.98047" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M10.33 16.5H13.66" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M9.5 12.5H14.5" stroke="#565858" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {tooltipVisible && !canDelete && (
              <div className="absolute top-[-48px] right-0 bg-[rgba(13,13,13,0.8)] px-[16px] py-[8px] rounded-[12px] whitespace-nowrap z-10">
                <p className="font-segoe text-[13px] leading-[18px] text-white">{t('paidBanner.title')}</p>
              </div>
            )}
          </button>
        </div>

        {artifact.status === 'pending' && (
          <div className="flex items-center gap-[12px] py-[24px]">
            <div className="w-[24px] h-[24px] border-2 border-[#2197f1] border-t-transparent rounded-full animate-spin" />
            <p className="font-segoe text-[16px] leading-[24px] text-[#565858]">{t('history.pending')}</p>
          </div>
        )}

        {artifact.status === 'failed' && (
          <p className="font-segoe text-[16px] leading-[24px] text-[#c0392b]">{t('history.failed')}</p>
        )}

        {artifact.status === 'completed' && artifact.content && (
          <>
            <p className="font-segoe text-[16px] leading-[24px] text-[#0d0d0d]">
              {artifact.content.summary}
            </p>

            {ViewComponent && <ViewComponent methodId={methodId} content={artifact.content} />}
            {methodId === 'hadi' && onReview && (
              <Button variant="outline" onClick={onReview}>{t('hadi.reviewButton')}</Button>
            )}

            {artifact.content.nextSteps.length > 0 && (
              <div className="flex flex-col gap-[12px]">
                <h3 className="font-manrope font-bold text-[20px] leading-[28px] text-[#0d0d0d]">
                  {t('detail.nextSteps')}
                </h3>
                <ul className="flex flex-col gap-[8px]">
                  {artifact.content.nextSteps.map((step, index) => (
                    <li key={index} className="font-segoe text-[14px] leading-[20px] text-[#0d0d0d] pl-[16px] relative">
                      <span className="absolute left-0 top-[8px] w-[4px] h-[4px] rounded-full bg-[#2197f1]" />
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {artifact.generated_text && (
              <div>
                <PdfExportButton
                  data={{
                    title,
                    content: artifact.generated_text,
                    artifactType: `methodology_${methodId}`,
                    artifactTypeLabel: t('detail.pdfLabel'),
                    projectName: projectName || t('detail.defaultProjectName'),
                    date: artifact.created,
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
